import React, { useEffect, useState, useCallback, useMemo } from "react";
import Sidebar from "./Sidebar";
import Navbar from "./Navbar";

const API_BASE = `${process.env.REACT_APP_API_BASE_URL}/api`;
const USER_ID = 1;

const GraphFormsPage = () => {
  const [forms, setForms] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [sbCollapsed, setSbCollapsed] = useState(false);


  useEffect(() => {
    fetchFormsWithGraphs();
  }, []);

  const fetchFormsWithGraphs = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/forms/with-graphs`);
      const data = await response.json();
      setForms(data.data || []);
    } catch (error) {
      console.error("Error fetching forms with graphs:", error);
    }
    setLoading(false);
  };

  // Helper to flatten all nodes including subfields
  const flattenAllNodes = useCallback((nodes) => {
    const result = [];
    
    const flatten = (nodeList, parentId = null, parentPath = '') => {
      nodeList.forEach((node) => {
        // Create full path for this node
        const nodePath = parentPath ? `${parentPath}.${node.id}` : node.id;
        
        // Add the main node with its path
        result.push({
          ...node,
          parentId,
          fullPath: nodePath,
          displayPath: nodePath
        });
        
        // Add subfields recursively
        if (node.subfields && node.subfields.length > 0) {
          flatten(node.subfields, node.id, nodePath);
        }
        
        // Handle row columns
        if (node.type === 'row') {
          flatten(node.cols?.left || [], parentId, parentPath);
          flatten(node.cols?.right || [], parentId, parentPath);
        }
      });
    };
    
    flatten(nodes);
    return result;
  }, []);

  // Get all fields including subfields for the selected form
  const allFormFields = useMemo(() => {
    return selectedForm ? flattenAllNodes(selectedForm.schema || []) : [];
  }, [selectedForm, flattenAllNodes]);

  // Helper to get field value from formData, checking multiple possible paths
  const getFieldValue = useCallback((fieldId) => {
    if (!fieldId) return null;
    
    // First try direct field ID
    let value = formData[fieldId];
    
    // If not found, check all possible path variations
    if (value === undefined) {
      // Find the field in allFormFields to get its path info
      const field = allFormFields.find(f => f.id === fieldId);
      if (field) {
        // Try different variations of the field path
        const possiblePaths = [
          field.fullPath,                // Full hierarchical path
          field.id,                      // Just the field ID
          field.parentId ? `${field.parentId}.${field.id}` : field.id, // Parent.child format
        ];
        
        // Also try to find by traversing parent hierarchy
        if (field.parentId) {
          const parentField = allFormFields.find(f => f.id === field.parentId);
          if (parentField) {
            possiblePaths.push(`${parentField.fullPath}.${field.id}`);
            possiblePaths.push(`${parentField.id}.${field.id}`);
          }
        }
        
        // Check all possible paths
        for (const path of possiblePaths) {
          if (formData[path] !== undefined) {
            value = formData[path];
            break;
          }
        }
      }
    }
    
    return value;
  }, [formData, allFormFields]);

  // Get all graph nodes from the form schema
  const graphNodes = useMemo(() => {
    if (!selectedForm || !selectedForm.schema) return [];
    
    // Flatten all nodes to find all graphs, including those in rows
    const allNodes = flattenAllNodes(selectedForm.schema);
    const graphs = allNodes.filter(node => node.type === "graph");
    
    return graphs;
  }, [selectedForm, flattenAllNodes]);

  // Prepare data for graph renderer
  const prepareGraphData = useCallback((graphNode) => {
    if (!graphNode) return { chartData: [] };

    const xFieldId = graphNode.xAxisField;
    const yFieldId = graphNode.yAxisField;

    // Find the actual field objects
    const xField = allFormFields.find(f => f.id === xFieldId);
    const yField = allFormFields.find(f => f.id === yFieldId);

    // Get values from formData
    const xValue = getFieldValue(xFieldId);
    const yValue = getFieldValue(yFieldId);

    // Parse values to numbers
    const parseValue = (val) => {
      if (val === undefined || val === null || val === '') {
        return 0;
      }
      const num = parseFloat(val);
      return isNaN(num) ? 0 : Math.max(0, num);
    };

    const xNum = parseValue(xValue);
    const yNum = parseValue(yValue);

    // Create chart data - for all chart types
    const chartData = [];
    
    if (xField || xFieldId) {
      chartData.push({ 
        label: xField ? xField.label : 'X Value', 
        value: xNum,
        color: '#1890ff'
      });
    }
    
    if (yField || yFieldId) {
      chartData.push({ 
        label: yField ? yField.label : 'Y Value', 
        value: yNum,
        color: '#52c41a'
      });
    }

    // Add more colors for stacked charts if needed
    const colors = ['#1890ff', '#52c41a', '#fa8c16', '#f5222d', '#722ed1', '#13c2c2'];

    return {
      chartData,
      xLabel: xField ? xField.label : 'X Axis',
      yLabel: yField ? yField.label : 'Y Axis',
      xValue: xNum,
      yValue: yNum,
      graphType: graphNode.graphType || 'bar',
      graphTitle: graphNode.label || 'Graph',
      colors
    };
  }, [formData, allFormFields, getFieldValue]);

  const openFormGraphs = async (form) => {
    setLoading(true);
    try {
      const formRes = await fetch(`${API_BASE}/forms/${form.id}`);
      const formJson = await formRes.json();

      const draftRes = await fetch(
        `${API_BASE}/form-submissions/${form.id}/${USER_ID}`
      );
      const draftJson = await draftRes.json();

      setSelectedForm(formJson.data);
      setFormData(draftJson.data || {});
    } catch (error) {
      console.error("Error opening form graphs:", error);
    }
    setLoading(false);
  };

  // Sidebar width (must match Sidebar component)
  const SIDEBAR_WIDTH = 260;

  // Function to render different graph types
  const renderGraph = (graphData, graphType) => {
    if (graphData.chartData.length === 0) {
      return (
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          color: "#999",
          fontStyle: "italic"
        }}>
          No data available
        </div>
      );
    }

    const { chartData, colors } = graphData;
    
    switch (graphType) {
      case 'doughnut':
      case 'donut':
        return renderDoughnutChart(chartData, colors);
      case 'pie':
        return renderPieChart(chartData, colors);
      case 'hbar':
      case 'vertical':
        return renderVerticalBarChart(chartData, colors);
      case 'stacked':
        return renderStackedBarChart(chartData, colors);
      case 'bar':
      default:
        return renderBarChart(chartData, colors);
    }
  };

  const renderDoughnutChart = (chartData, colors) => {
    const total = chartData.reduce((sum, item) => sum + item.value, 0);
    const radius = 70;
    const circumference = 2 * Math.PI * radius;
    
    let accumulated = 0;
    
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        padding: "20px"
      }}>
        <div style={{ position: "relative", width: "180px", height: "180px" }}>
          {/* Perfectly round doughnut chart */}
          <svg width="180" height="180" viewBox="0 0 180 180">
            <circle
              cx="90"
              cy="90"
              r={radius}
              fill="transparent"
              stroke="#f0f0f0"
              strokeWidth="25"
            />
            
            {chartData.map((item, index) => {
              const percentage = total > 0 ? (item.value / total) : 0;
              const strokeDasharray = `${circumference * percentage} ${circumference * (1 - percentage)}`;
              const strokeDashoffset = -accumulated * circumference;
              accumulated += percentage;
              
              return (
                <circle
                  key={index}
                  cx="90"
                  cy="90"
                  r={radius}
                  fill="transparent"
                  stroke={colors[index % colors.length]}
                  strokeWidth="25"
                  strokeDasharray={strokeDasharray}
                  strokeDashoffset={strokeDashoffset}
                  strokeLinecap="round"
                  transform="rotate(-90 90 90)"
                />
              );
            })}
            
            {/* Center hole for perfect doughnut */}
            <circle
              cx="90"
              cy="90"
              r={radius - 12}
              fill="white"
            />
            
            {/* Center text */}
            <text
              x="90"
              y="90"
              textAnchor="middle"
              dy="0.3em"
              fontSize="18"
              fontWeight="bold"
              fill="#333"
            >
              {total}
            </text>
            <text
              x="90"
              y="110"
              textAnchor="middle"
              fontSize="12"
              fill="#666"
            >
              Total
            </text>
          </svg>
        </div>
        
        {/* Legend */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
          gap: "8px",
          marginTop: "20px",
          width: "100%"
        }}>
          {chartData.map((item, index) => {
            const percentage = total > 0 ? Math.round((item.value / total) * 100) : 0;
            return (
              <div key={index} style={{
                display: "flex",
                alignItems: "center",
                fontSize: "12px",
                padding: "4px 8px",
                background: "#f8f9fa",
                borderRadius: "4px"
              }}>
                <div style={{
                  width: "12px",
                  height: "12px",
                  backgroundColor: colors[index % colors.length],
                  borderRadius: "50%",
                  marginRight: "8px"
                }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: "600" }}>{item.label}</div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span>{item.value}</span>
                    <span style={{ color: "#666" }}>{percentage}%</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderPieChart = (chartData, colors) => {
    const total = chartData.reduce((sum, item) => sum + item.value, 0);
    const radius = 70;
    
    let accumulatedAngle = 0;
    
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        padding: "20px"
      }}>
        <div style={{ position: "relative", width: "180px", height: "180px" }}>
          <svg width="180" height="180" viewBox="0 0 180 180">
            {chartData.map((item, index) => {
              const percentage = total > 0 ? (item.value / total) : 0;
              const angle = percentage * 360;
              const largeArcFlag = angle > 180 ? 1 : 0;
              
              const x1 = 90 + radius * Math.cos(accumulatedAngle * Math.PI / 180);
              const y1 = 90 + radius * Math.sin(accumulatedAngle * Math.PI / 180);
              
              accumulatedAngle += angle;
              
              const x2 = 90 + radius * Math.cos(accumulatedAngle * Math.PI / 180);
              const y2 = 90 + radius * Math.sin(accumulatedAngle * Math.PI / 180);
              
              const pathData = [
                `M 90 90`,
                `L ${x1} ${y1}`,
                `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
                `Z`
              ].join(' ');
              
              return (
                <path
                  key={index}
                  d={pathData}
                  fill={colors[index % colors.length]}
                  stroke="white"
                  strokeWidth="2"
                />
              );
            })}
            
            {/* Center text */}
            <text
              x="90"
              y="90"
              textAnchor="middle"
              dy="0.3em"
              fontSize="18"
              fontWeight="bold"
              fill="white"
              stroke="#333"
              strokeWidth="1"
            >
              {total}
            </text>
          </svg>
        </div>
        
        {/* Legend */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
          gap: "8px",
          marginTop: "20px",
          width: "100%"
        }}>
          {chartData.map((item, index) => {
            const percentage = total > 0 ? Math.round((item.value / total) * 100) : 0;
            return (
              <div key={index} style={{
                display: "flex",
                alignItems: "center",
                fontSize: "12px",
                padding: "4px 8px",
                background: "#f8f9fa",
                borderRadius: "4px"
              }}>
                <div style={{
                  width: "12px",
                  height: "12px",
                  backgroundColor: colors[index % colors.length],
                  borderRadius: "2px",
                  marginRight: "8px"
                }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: "600" }}>{item.label}</div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span>{item.value}</span>
                    <span style={{ color: "#666" }}>{percentage}%</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderBarChart = (chartData, colors) => {
    const maxValue = Math.max(...chartData.map(item => item.value), 1);
    
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        padding: "20px"
      }}>
        <div style={{
          flex: 1,
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-around",
          padding: "10px 0"
        }}>
          {chartData.map((item, idx) => (
            <div key={idx} style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              height: "100%"
            }}>
              <div style={{
                width: "50px",
                height: `${(item.value / maxValue) * 80}%`,
                background: colors[idx % colors.length],
                borderRadius: "4px 4px 0 0",
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "center",
                paddingBottom: "5px",
                color: "white",
                fontWeight: "bold",
                fontSize: "14px",
                transition: "height 0.3s ease",
                minHeight: "30px"
              }}>
                {item.value}
              </div>
              <div style={{
                fontSize: "12px",
                fontWeight: "600",
                color: "#333",
                textAlign: "center",
                marginTop: "8px",
                padding: "0 5px"
              }}>
                {item.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderVerticalBarChart = (chartData, colors) => {
    const maxValue = Math.max(...chartData.map(item => item.value), 1);
    const barWidth = 60;
    const chartHeight = 120;
    
    return (
      <div style={{
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        height: "100%",
        padding: "20px",
        gap: "30px"
      }}>
        {chartData.map((item, idx) => (
          <div key={idx} style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center"
          }}>
            <div style={{
              width: `${barWidth}px`,
              height: `${(item.value / maxValue) * chartHeight}px`,
              background: colors[idx % colors.length],
              borderRadius: "4px 4px 0 0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontWeight: "bold",
              fontSize: "14px",
              transition: "height 0.3s ease",
              minHeight: "30px"
            }}>
              {item.value}
            </div>
            <div style={{
              fontSize: "12px",
              fontWeight: "600",
              color: "#333",
              textAlign: "center",
              marginTop: "8px",
              padding: "0 5px",
              maxWidth: "80px",
              wordBreak: "break-word"
            }}>
              {item.label}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderStackedBarChart = (chartData, colors) => {
    const total = chartData.reduce((sum, item) => sum + item.value, 0);
    
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        padding: "20px"
      }}>
        <div style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center"
        }}>
          <div style={{
            width: "80%",
            height: "60px",
            background: "#f0f0f0",
            borderRadius: "4px",
            overflow: "hidden",
            margin: "0 auto",
            position: "relative"
          }}>
            {chartData.reduce((acc, item, idx) => {
              const percentage = total > 0 ? (item.value / total) * 100 : 0;
              const width = `${percentage}%`;
              const left = `${acc}%`;
              
              acc += percentage;
              
              return (
                <React.Fragment key={idx}>
                  <div style={{
                    position: "absolute",
                    left: left,
                    width: width,
                    height: "100%",
                    background: colors[idx % colors.length],
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "14px"
                  }}>
                    {percentage > 15 ? `${Math.round(percentage)}%` : ''}
                  </div>
                  {acc}
                </React.Fragment>
              );
            }, 0)}
          </div>
          
          {/* Legend */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
            gap: "8px",
            marginTop: "30px"
          }}>
            {chartData.map((item, index) => {
              const percentage = total > 0 ? Math.round((item.value / total) * 100) : 0;
              return (
                <div key={index} style={{
                  display: "flex",
                  alignItems: "center",
                  fontSize: "12px",
                  padding: "6px 10px",
                  background: "#f8f9fa",
                  borderRadius: "4px"
                }}>
                  <div style={{
                    width: "12px",
                    height: "12px",
                    backgroundColor: colors[index % colors.length],
                    borderRadius: "2px",
                    marginRight: "8px"
                  }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: "600" }}>{item.label}</div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span>{item.value}</span>
                      <span style={{ color: "#666" }}>{percentage}%</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Helper function to get graph type display name
  const getGraphTypeDisplayName = (graphType) => {
    const typeMap = {
      'bar': 'Bar Chart',
      'hbar': 'Vertical Bar',
      'vertical': 'Vertical Bar',
      'stacked': 'Stacked Bar',
      'doughnut': 'Doughnut',
      'donut': 'Doughnut',
      'pie': 'Pie Chart'
    };
    
    return typeMap[graphType] || 'Chart';
  };

  // Sidebar width (must match Sidebar component)
 

  if (!selectedForm) {
    return (
      <div style={{ display: "flex", minHeight: "100vh" }}>
        <Navbar onMenuClick={() => setSbCollapsed(!sbCollapsed)} />
        <Sidebar collapsed={sbCollapsed} setCollapsed={setSbCollapsed} />

        <div
          style={{
            flex: 1,
            padding: "30px",
            marginLeft: sbCollapsed ? '80px' : SIDEBAR_WIDTH,
            transition: 'margin-left 0.3s ease',
            background: "#f5f6fa",
          }}
        >
          <h2 style={{ marginBottom: 20 }}>📊 Forms With Graphs</h2>

          {loading ? (
            <p>Loading forms...</p>
          ) : forms.length === 0 ? (
            <p>No forms with graphs available.</p>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                gap: 20,
              }}
            >
              {forms.map((f) => (
                <div
                  key={f.id}
                  onClick={() => openFormGraphs(f)}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    height: 150,
                    background: "#bcdbf5",
                    border: "1px solid #0d18f4",
                    borderRadius: 12,
                    cursor: "pointer",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                    transition: "all 0.2s",
                    padding: 10,
                    textAlign: "center",
                    fontWeight: 600,
                    color: "#0a0a0a",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.boxShadow = "0 6px 12px rgba(0,0,0,0.15)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.boxShadow = "0 2px 6px rgba(0,0,0,0.1)")
                  }
                >
                  {f.form_name}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ------------------ Selected Form View ------------------
  return (
    <div style={{ display: "flex", minHeight: "100vh" }}>
      <Navbar onMenuClick={() => setSbCollapsed(!sbCollapsed)} />
      <Sidebar collapsed={sbCollapsed} setCollapsed={setSbCollapsed} />

      <div
        style={{
          flex: 1,
          padding: "30px",
          marginLeft: sbCollapsed ? '80px' : SIDEBAR_WIDTH,
          transition: 'margin-left 0.3s ease',
          background: "#f5f6fa",
        }}
      >
        <button
          onClick={() => {
            setSelectedForm(null);
            setFormData({});
          }}
          style={{
            padding: "8px 16px",
            marginBottom: 20,
            border: "none",
            borderRadius: 6,
            background: "#007bff",
            color: "#fff",
            cursor: "pointer",
            transition: "background 0.3s ease"
          }}
          onMouseEnter={(e) => e.target.style.background = "#0056b3"}
          onMouseLeave={(e) => e.target.style.background = "#007bff"}
        >
          ← Back to Forms
        </button>

        <h2 style={{ marginBottom: 20, color: "#333" }}>{selectedForm.form_name}</h2>

        {loading ? (
          <p>Loading graphs...</p>
        ) : graphNodes.length === 0 ? (
          <div style={{
            padding: "40px",
            textAlign: "center",
            background: "#fff",
            borderRadius: "8px",
            border: "1px solid #e0e0e0"
          }}>
            <p style={{ fontSize: "16px", color: "#666" }}>No graphs found in this form.</p>
          </div>
        ) : (
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(380px, 1fr))",
            gap: 25
          }}>
            {graphNodes.map((graphNode, index) => {
              const graphData = prepareGraphData(graphNode);
              const graphType = graphNode.graphType || 'bar';
              
              return (
                <div key={graphNode.id || index} style={{
                  background: "#fff",
                  borderRadius: "12px",
                  padding: "20px",
                  border: "1px solid #e0e0e0",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  display: "flex",
                  flexDirection: "column"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-4px)";
                  e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.05)";
                }}>
                  {/* Graph Header */}
                  <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "16px",
                    paddingBottom: "12px",
                    borderBottom: "1px solid #f0f0f0"
                  }}>
                    <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "600" }}>
                      {graphType === 'doughnut' ? '' : 
                       graphType === 'pie' ? '' : 
                       graphType === 'hbar' ? '' : 
                       graphType === 'stacked' ? '' : ''} {graphData.graphTitle || `Graph ${index + 1}`}
                    </h3>
                    <span style={{
                      padding: "4px 10px",
                      background: "#e8f4fd",
                      borderRadius: "20px",
                      fontSize: "12px",
                      fontWeight: "500",
                      color: "#1890ff"
                    }}>
                      {getGraphTypeDisplayName(graphType)}
                    </span>
                  </div>

                  {/* Data Summary */}
                  {graphData.chartData.length > 0 && (
                    <div style={{
                      marginBottom: "16px",
                      padding: "12px",
                      background: "#f8f9fa",
                      borderRadius: "8px",
                      fontSize: "13px"
                    }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                        <span style={{ color: "#666" }}>
                          <strong>{graphData.xLabel}:</strong>
                        </span>
                        <span style={{ fontWeight: "600", color: "#1890ff" }}>
                          {graphData.xValue}
                        </span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span style={{ color: "#666" }}>
                          <strong>{graphData.yLabel}:</strong>
                        </span>
                        <span style={{ fontWeight: "600", color: "#52c41a" }}>
                          {graphData.yValue}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Graph Visualization */}
                  <div style={{ 
                    height: graphType === 'doughnut' || graphType === 'pie' ? "280px" : "220px", 
                    position: "relative",
                    flex: 1
                  }}>
                    {renderGraph(graphData, graphType)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default GraphFormsPage;